import * as React from "react"
import { ArrowRightIcon } from "@/components/brand-kit/icon-sprite"
import Link from "next/link"

interface SectionHeaderProps {
  eyebrow: string
  title: string
  description?: string
  /** Optional right-side "See all" action — renders as orange link with trailing
   *  arrow. Either an `href` (Link) or `onClick` (button) — exactly one. */
  action?:
    | { type: "link"; label: string; href: string }
    | { type: "button"; label: string; onClick: () => void }
  /** Bottom margin (default `mb-6` so cards/grids drop into place comfortably). */
  className?: string
}

/**
 * Section header used across every block on the brand detail page.
 *
 * Eyebrow renders as a blue→orange gradient text label (handoff §3). Title is
 * 26px / 900 / tight tracking. Optional right-side action shows up as an
 * orange "See all →" link with a 6→9px gap shift on hover that gives the
 * arrow a subtle "step forward" motion.
 */
export function SectionHeader({
  eyebrow,
  title,
  description,
  action,
  className = "mb-6",
}: SectionHeaderProps) {
  return (
    <header className={`flex flex-wrap items-end justify-between gap-x-6 gap-y-2 ${className}`}>
      <div className="min-w-0">
        {/* Eyebrow is optional — passing an empty string hides it entirely
            so callers can decide per-section whether the gradient micro-
            label is worth the visual weight (the brand-detail catalog
            header drops it because the H1 already names the section). */}
        {eyebrow && (
          <p
            className="text-[11px] font-extrabold uppercase tracking-[0.14em]"
            style={{
              backgroundImage: "linear-gradient(95deg, #0469f0, #ff9951)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            {eyebrow}
          </p>
        )}
        <h2 className={`${eyebrow ? "mt-1.5" : ""} text-[26px] font-black tracking-[-0.025em] text-gray-900`}>
          {title}
        </h2>
        {description && (
          <p className="mt-1 max-w-[680px] text-[14px] text-gray-600">{description}</p>
        )}
      </div>
      {action && (
        <SeeAllAction action={action} />
      )}
    </header>
  )
}

function SeeAllAction({
  action,
}: {
  action: NonNullable<SectionHeaderProps["action"]>
}) {
  // 6→9px gap on hover gives the arrow a subtle "→" forward step without
  // animating the icon itself.
  const baseClasses =
    "group inline-flex items-center gap-1.5 text-[13px] font-bold text-[#ff9951] transition-[gap] duration-150 hover:gap-2.5"
  if (action.type === "link") {
    // FRIDGE-1261: strictNullChecks is off in this repository, so the type above
    // cannot stop a null href; next/link throws on one and the page render dies.
    // No destination means no link.
    if (typeof action.href !== "string" || action.href === "") return null
    return (
      <Link href={action.href} className={baseClasses}>
        {action.label}
        <ArrowRightIcon className="h-3 w-3" aria-hidden />
      </Link>
    )
  }
  return (
    <button type="button" onClick={action.onClick} className={baseClasses}>
      {action.label}
      <ArrowRightIcon className="h-3 w-3" aria-hidden />
    </button>
  )
}
